const dockerCheatsheet = {
  id: "docker",
  title: "Docker Cheatsheet",
  description:
    "A comprehensive guide to Docker including containers, images, networking, volumes, and best practices",
  language: "bash",
  category: "DevOps",
  level: "All Levels",
  sections: [
    {
      title: "Getting Started",
      items: [
        {
          title: "Installation & Setup",
          content: "Docker installation and verification",
          code: `# Install Docker (Ubuntu)
sudo apt-get update
sudo apt-get install docker-ce docker-ce-cli containerd.io

# Verify installation
docker --version
docker info
docker system info

# Start Docker service
sudo systemctl start docker
sudo systemctl enable docker

# Run test container
docker run hello-world`,
          note: "Basic Docker installation and verification commands",
        },
        {
          title: "Basic Commands",
          content: "Essential Docker commands",
          code: `# Docker help
docker --help
docker container --help
docker image --help

# Check Docker status
docker version
docker info
docker system df        # Show Docker disk usage
docker system prune     # Remove unused data
docker system prune -a  # Remove all unused data

# Login to Docker Hub
docker login
docker logout`,
          note: "Common Docker commands for daily use",
        },
      ],
    },
    {
      title: "Container Management",
      items: [
        {
          title: "Running Containers",
          content: "Commands for container operations",
          code: `# Run containers
docker run nginx                # Run container
docker run -d nginx            # Run in background
docker run -p 8080:80 nginx    # Port mapping
docker run --name web nginx    # Named container
docker run -v /host:/container nginx  # Volume mounting
docker run --rm nginx          # Auto-remove after stop

# Container with environment variables
docker run -e DB_HOST=localhost \\
          -e DB_USER=admin \\
          -e DB_PASS=secret \\
          mysql:8.0

# Run with resource limits
docker run --memory="512m" \\
          --cpus="1.5" \\
          nginx

# Run with restart policies
docker run --restart=always nginx    # Always restart
docker run --restart=on-failure:3 nginx  # Restart max 3 times`,
          note: "Different ways to run Docker containers",
        },
        {
          title: "Container Operations",
          content: "Managing running containers",
          code: `# List containers
docker ps                # List running containers
docker ps -a             # List all containers
docker ps -q             # Only container IDs
docker ps --format "table {{.ID}}\\t{{.Names}}\\t{{.Status}}"

# Container lifecycle
docker start container-name    # Start container
docker stop container-name     # Stop container
docker restart container-name  # Restart container
docker pause container-name    # Pause container
docker unpause container-name  # Unpause container
docker kill container-name     # Force stop

# Container information
docker logs container-name     # View logs
docker logs -f container-name  # Follow logs
docker top container-name      # Show processes
docker stats container-name    # Resource usage
docker inspect container-name  # Detailed info

# Execute commands
docker exec -it container-name bash  # Interactive shell
docker exec container-name ls /app   # Run single command
docker exec -e VAR=value container-name command  # With env vars`,
          note: "Commands for managing running containers",
        },
        {
          title: "Container Cleanup",
          content: "Removing containers and cleanup",
          code: `# Remove containers
docker rm container-name      # Remove stopped container
docker rm -f container-name   # Force remove running container
docker rm $(docker ps -aq)    # Remove all containers

# Cleanup commands
docker container prune        # Remove stopped containers
docker container prune -f     # Force remove without confirmation

# Remove containers by status
docker rm $(docker ps -q -f status=exited)  # Remove exited
docker rm $(docker ps -q -f status=created)  # Remove created`,
          note: "Commands for container cleanup and maintenance",
        },
      ],
    },
    {
      title: "Image Management",
      items: [
        {
          title: "Working with Images",
          content: "Managing Docker images",
          code: `# List images
docker images            # List all images
docker images -a         # Include intermediates
docker images -q         # Only image IDs
docker images --digests  # Show digests

# Pull images
docker pull nginx              # Latest version
docker pull nginx:1.19.3       # Specific version
docker pull --all-tags nginx   # All tags

# Image information
docker image inspect nginx     # Detailed info
docker history nginx          # Layer history

# Remove images
docker rmi image-name         # Remove image
docker rmi -f image-name      # Force remove
docker rmi $(docker images -q) # Remove all images

# Tag images
docker tag nginx:latest myregistry.com/nginx:v1
docker tag image-id myname/app:v1`,
          note: "Commands for managing Docker images",
        },
        {
          title: "Building Images",
          content: "Creating custom Docker images",
          code: `# Basic Dockerfile
FROM node:14
WORKDIR /app
COPY package*.json ./
RUN npm install
COPY . .
EXPOSE 3000
CMD ["npm", "start"]

# Build image
docker build -t myapp:1.0 .   # Build with tag
docker build --no-cache .     # Build without cache
docker build -f custom.dockerfile .  # Custom file

# Multi-stage builds
FROM node:14 AS builder
WORKDIR /app
COPY package*.json ./
RUN npm install
COPY . .
RUN npm run build

FROM nginx:alpine
COPY --from=builder /app/build /usr/share/nginx/html`,
          note: "Creating and building Docker images",
        },
      ],
    },
    {
      title: "Docker Networking",
      items: [
        {
          title: "Network Management",
          content: "Managing Docker networks",
          code: `# List networks
docker network ls
docker network inspect bridge

# Create network
docker network create my-network
docker network create --driver overlay my-network
docker network create --subnet=172.18.0.0/16 my-network

# Connect containers
docker network connect my-network container1
docker network disconnect my-network container1

# Run container with network
docker run --network=my-network nginx
docker run --network=host nginx  # Host networking
docker run --network=none nginx  # No networking`,
          note: "Managing Docker networks and connectivity",
        },
        {
          title: "Network Troubleshooting",
          content: "Debugging network issues",
          code: `# Inspect network
docker network inspect bridge

# Container networking
docker port container-name    # Show port mappings
docker run --dns=8.8.8.8 nginx  # Custom DNS
docker run --add-host=host:ip nginx  # Add host entry

# Network cleanup
docker network prune          # Remove unused networks
docker network rm my-network  # Remove specific network`,
          note: "Commands for network troubleshooting",
        },
      ],
    },
    {
      title: "Data Management",
      items: [
        {
          title: "Volumes",
          content: "Managing persistent data",
          code: `# Create volumes
docker volume create my-vol
docker volume create --driver local \\
    --opt type=none \\
    --opt device=/path/on/host \\
    --opt o=bind my-vol

# List and inspect volumes
docker volume ls
docker volume inspect my-vol

# Use volumes
docker run -v my-vol:/app/data nginx
docker run --mount source=my-vol,target=/app/data nginx

# Remove volumes
docker volume rm my-vol
docker volume prune          # Remove unused volumes`,
          note: "Managing Docker volumes for persistent data",
        },
        {
          title: "Bind Mounts",
          content: "Mounting host directories",
          code: `# Bind mount examples
docker run -v /host/path:/container/path nginx
docker run --mount type=bind,source=/host,target=/container nginx

# Read-only mounts
docker run -v /host/path:/container/path:ro nginx
docker run --mount type=bind,source=/host,target=/container,readonly nginx`,
          note: "Using bind mounts for development",
        },
      ],
    },
    {
      title: "Docker Compose",
      items: [
        {
          title: "Basic Compose",
          content: "Docker Compose fundamentals",
          code: `# docker-compose.yml example
version: '3.8'
services:
  web:
    image: nginx:alpine
    ports:
      - "80:80"
    volumes:
      - ./html:/usr/share/nginx/html
    depends_on:
      - api
  api:
    build: ./api
    environment:
      - DB_HOST=db
    depends_on:
      - db
  db:
    image: postgres:13
    environment:
      - POSTGRES_PASSWORD=secret

# Compose commands
docker-compose up         # Start services
docker-compose up -d      # Start in background
docker-compose down       # Stop and remove
docker-compose ps         # List services
docker-compose logs       # View logs
docker-compose exec web bash  # Run command`,
          note: "Working with Docker Compose",
        },
        {
          title: "Advanced Compose",
          content: "Advanced Compose features",
          code: `# Advanced compose features
version: '3.8'
services:
  web:
    build:
      context: ./frontend
      dockerfile: Dockerfile.prod
    deploy:
      replicas: 3
      resources:
        limits:
          cpus: '0.5'
          memory: 512M
    healthcheck:
      test: ["CMD", "curl", "-f", "http://localhost"]
      interval: 30s
      timeout: 10s
      retries: 3
    networks:
      - frontend
      - backend

networks:
  frontend:
  backend:

volumes:
  data:`,
          note: "Advanced Docker Compose configurations",
        },
      ],
    },
    {
      title: "Best Practices",
      items: [
        {
          title: "Security Best Practices",
          content: "Securing Docker containers",
          code: `# Use official base images
FROM node:14-alpine

# Run as non-root user
RUN addgroup -S appgroup && adduser -S appuser -G appgroup
USER appuser

# Scan images for vulnerabilities
docker scan myapp:1.0

# Use multi-stage builds to reduce size
FROM node:14 AS builder
# ... build steps ...

FROM node:14-alpine
COPY --from=builder /app/dist ./dist

# Use specific versions
FROM nginx:1.19.3-alpine

# Limit container resources
docker run --memory="512m" --cpus="1.5" nginx`,
          note: "Security best practices for Docker",
        },
        {
          title: "Performance Optimization",
          content: "Optimizing Docker performance",
          code: `# Optimize Dockerfile
# Use .dockerignore
node_modules
npm-debug.log
Dockerfile
.git
.env

# Layer optimization
FROM node:14-alpine
WORKDIR /app
COPY package*.json ./
RUN npm ci --only=production
COPY . .

# Cache bust when needed
FROM nginx
COPY --from=builder /app/dist /usr/share/nginx/html
ARG BUILD_DATE
LABEL build_date=$BUILD_DATE

# Use appropriate base images
FROM alpine:3.14  # Minimal base
FROM node:14-alpine  # Minimal Node.js`,
          note: "Optimizing Docker performance and build time",
        },
      ],
    },
  ],
};

export default dockerCheatsheet;
